import { type RefObject, useEffect, useMemo, useState } from 'react';

import { useThrottledCallbackOnScroll } from './useThrottledCallbackOnScroll.js';
import { throttleRaf } from '../utils/throttleRaf.js';

export const usePointerAndScrollResponsiveChartBounds = (
  chartRef: RefObject<HTMLElement | null> | undefined,
  enabled: boolean,
): (() => DOMRect | undefined) => {
  const getBoundingClientRect = () => () =>
    chartRef?.current?.getBoundingClientRect();
  const [getChartBounds, setGetChartBounds] = useState<
    () => DOMRect | undefined
  >(getBoundingClientRect);

  useThrottledCallbackOnScroll(!enabled, () => {
    setGetChartBounds(getBoundingClientRect);
  });
  const throttledPointerUpdate = useMemo(
    () =>
      throttleRaf(() => {
        setGetChartBounds(getBoundingClientRect);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const throttledMoveUpdate = useMemo(
    () =>
      throttleRaf((event: PointerEvent) => {
        if (
          !event.pressure ||
          chartRef?.current?.contains(event.target as Node)
        ) {
          return;
        }
        setGetChartBounds(getBoundingClientRect);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    window.addEventListener('pointermove', throttledMoveUpdate);
    window.addEventListener('pointerup', throttledPointerUpdate);
    return () => {
      window.removeEventListener('pointermove', throttledMoveUpdate);
      window.removeEventListener('pointerup', throttledPointerUpdate);
    };
  }, [throttledMoveUpdate, throttledPointerUpdate]);

  return getChartBounds;
};
